import { format } from "date-fns";
import { Modal } from "../../ui/modal";
import Badge from "../../ui/badge/Badge";
import Button from "../../ui/button/Button";
import { PayLink } from "../types";
import { CopyPaymentLinkModal } from "./CopyPaymentLinkModal";
import { useEffect, useState } from "react";
import { request } from "../../../utils";
import { UpdatePaymentLinkModal } from "./UpdatePaymentLinkModal";
import { usePaymentLinkStore } from "../../../stores/usePaymentLinkStore";
import { PAYMENTLINK_STATUS } from "../../../utils/constant";
import { getPaymentlinkStatusBadgeColor } from "../../../utils/helpers";

interface PaymentLinkDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  payLink: PayLink | null;
}

export default function PaymentLinkDetailsModal({
  isOpen,
  onClose,
  payLink: initialPayLink,
}: PaymentLinkDetailsModalProps) {
  const [currentPayLink, setCurrentPayLink] = useState<PayLink | null>(
    initialPayLink,
  );
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  // ---- cancel payment link state
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const { updatePaymentLink } = usePaymentLinkStore();

  // ---- load payment link
  useEffect(() => {
    setCurrentPayLink(initialPayLink);
  }, [initialPayLink]);

  if (!currentPayLink) return null;

  const paymentLink = `${
    import.meta.env.VITE_PAYLINK_BASE_URL
  }/payment-link/pay?token=${currentPayLink.token}`;
  const amount = `${
    currentPayLink.paymentDetails?.currency || currentPayLink.currencyIso2
  } ${currentPayLink.amount.toLocaleString()}`;

  // ---- cancel payment link
  const handleCancelPaymentLink = async () => {
    if (!currentPayLink._id) {
      setCancelError("Payment link not found");
      return; // Prevent further execution
    }

    setIsCancelling(true);
    setCancelError(null); // Clear previous errors

    try {
      const response = await request.post({
        url: `/payment-link/${currentPayLink._id}/cancel`,
      });

      if (response.success) {
        updatePaymentLink(currentPayLink._id, {
          status: PAYMENTLINK_STATUS.CANCELLED,
        });
        setShowCancelModal(false); // Only close if successful
        onClose?.(); // Optional: trigger parent cleanup
      } else {
        setCancelError("Failed to cancel payment link");
      }
    } catch (error) {
      console.error("Error cancelling payment link:", error);
      setCancelError("An unexpected error occurred while cancelling");
    } finally {
      setIsCancelling(false);
    }
  };

  // ---- update payment link
  const handleUpdateSuccess = (updatedLink: PayLink) => {
    updatePaymentLink(updatedLink._id, updatedLink);
    setCurrentPayLink(updatedLink);
    setShowUpdateModal(false);
  };

  // ---- get bank label
  const getBankLabel = (currency?: string) => {
    if (currency === "USD") return "Routing No:";
    if (currency === "GBP") return "Sort Code:";
    return "Provider/Bank:";
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className="max-w-lg mt-15">
        <div className="p-6 space-y-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white/90">
            Payment Link Details
          </h3>

          <div className="max-h-[70vh] overflow-y-auto overflow-x-hidden pr-2 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Amount</span>
              <span className="text-gray-800 dark:text-white/90">
                {currentPayLink.currency?.symbol}
                {currentPayLink.amount.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              <Badge
                size="sm"
                color={getPaymentlinkStatusBadgeColor(currentPayLink.status)}
              >
                {currentPayLink.status}
              </Badge>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Link</span>
              <span className="text-gray-800 dark:text-white/90">
                <button
                  onClick={() => setShowCopyModal(true)}
                  className="p-1 text-blue-600 hover:underline text-theme-sm dark:text-blue-400"
                  title="Copy link"
                >
                  View Link
                </button>
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">
                Created At
              </span>
              <span className="text-gray-800 dark:text-white/90">
                {format(
                  new Date(currentPayLink.createdAt),
                  "MMMM do yyyy, h:mm a",
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">
                Updated At
              </span>
              <span className="text-gray-800 dark:text-white/90">
                {format(
                  new Date(currentPayLink.updatedAt),
                  "MMMM do yyyy, h:mm a",
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">
                Expires At
              </span>
              <span className="text-gray-800 dark:text-white/90">
                {format(
                  new Date(
                    currentPayLink.expiresAt ||
                      new Date(
                        new Date(currentPayLink.createdAt).getTime() +
                          30 * 24 * 60 * 60 * 1000,
                      ),
                  ),
                  "MMMM do yyyy, h:mm a",
                )}
              </span>
            </div>

            {currentPayLink.status === PAYMENTLINK_STATUS.DECLINED &&
              currentPayLink.declineReason && (
                <div className="border-t pt-4 mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Decline Reason
                  </h4>
                  <p className="text-gray-800 dark:text-white/90 whitespace-pre-line">
                    {currentPayLink.declineReason}
                  </p>
                </div>
              )}

            {currentPayLink.displayMessage && (
              <div className="border-t pt-4 mt-4 space-y-2">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Message
                </h4>
                <p className="text-gray-800 dark:text-white/90 whitespace-pre-line">
                  {currentPayLink.displayMessage}
                </p>
              </div>
            )}

            {currentPayLink.payerDetails && (
              <div className="border-t pt-4 mt-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Payer Details
                </h4>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Name</span>
                  <span className="text-gray-800 dark:text-white/90">
                    {currentPayLink.payerDetails.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">
                    Email
                  </span>
                  <span className="text-gray-800 dark:text-white/90">
                    {currentPayLink.payerDetails.email}
                  </span>
                </div>
              </div>
            )}

            {currentPayLink.paymentDetails && (
              <div className="border-t pt-4 mt-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Receiving Details
                </h4>

                {currentPayLink.paymentDetails.paymentMethod ===
                "STABLE_CURRENCY" ? (
                  // Stable Currency Details
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Currency
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {currentPayLink.paymentDetails.currency ||
                          currentPayLink.paymentDetails.iso2}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Network
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {currentPayLink.paymentDetails.network}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Wallet Address
                      </span>
                      <span className="text-gray-800 dark:text-white/90 break-all text-sm">
                        {currentPayLink.paymentDetails.walletAddress}
                      </span>
                    </div>
                  </>
                ) : ["WECHAT_PAY", "ALIPAY"].includes(
                    currentPayLink.paymentDetails.paymentMethod,
                  ) ? (
                  // WeChat Pay & Alipay
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Account Name
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {currentPayLink.paymentDetails.accountName}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Phone Number
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {currentPayLink.paymentDetails.phoneNumber}
                      </span>
                    </div>
                  </>
                ) : (
                  // Traditional Bank Account Details
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Account Name
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {currentPayLink.paymentDetails.accountName}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Account Number
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {currentPayLink.paymentDetails.accountNumber}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">
                        {getBankLabel(currentPayLink.paymentDetails.currency)}
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {currentPayLink.paymentDetails.providerName}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Action buttons — fixed at bottom */}
          <div className="flex justify-between pt-4 border-t">
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => setShowCancelModal(true)}
                disabled={currentPayLink.status !== "PENDING"}
              >
                Cancel Payment Link
              </Button>
              <Button
                size="sm"
                onClick={() => setShowUpdateModal(true)}
                disabled={currentPayLink.status !== "PENDING"}
              >
                Update Payment Link
              </Button>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---- CopyPaymentLinkModal for copy links */}
      <CopyPaymentLinkModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        paymentLink={paymentLink}
        amount={amount}
      />

      {/* Cancel Payment Link  */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        className="max-w-sm"
      >
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Cancel Payment Link?
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to cancel this payment link? This action
            cannot be undone.
          </p>

          {cancelError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {cancelError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCancelModal(false)}
            >
              No
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={handleCancelPaymentLink}
              disabled={isCancelling}
              className={`px-6 py-2 rounded-lg text-white transition-colors ${
                isCancelling
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {isCancelling ? "Cancelling..." : "Yes, Cancel"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Update Payment Link Modal */}
      <UpdatePaymentLinkModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        payLink={currentPayLink}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </>
  );
}
